import {
  DataTableOptions,
  DataType,
  FilterType,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  CustomAction,
  DataTableColumn,
  GlobDataTable,
  GroupCustomAction,
  RowCustomAction
} from '@globaleris/ui.library/build/components/globalerisDataTable'
import { makeStyles, Menu, MenuItem, Typography, useTheme } from '@material-ui/core'
import {
  Autorenew,
  Block,
  Brush,
  CheckCircleOutline,
  CloudDownload,
  Delete,
  DoneAllOutlined,
  Edit,
  LoupeOutlined,
  Message,
  Undo,
  Warning
} from '@material-ui/icons'
import { Rating } from '@material-ui/lab'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumEstadoFactura,
  EnumTipoAgrupacion,
  EnumTipoCicloFacturacion,
  EnumTipoFactura,
  EnumTipoFacturacion,
  EnumTipoRefacturacion,
  EnumTipoSujeto
} from '../../../../api'
import { SettlementTableModel } from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumFileExtension } from '../../../../api/Pioneer/enums/enumFileExtension'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import { CardBottom } from '../../../../components/shared/cardBottom/cardBottom'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../helpers'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { mapInvoiceIdsToDownloadFileRequestModel } from '../../../documents/documents.mapper'
import { downloadFiles, downloadInvoiceDocument } from '../../../documents/documents.service'
import { EnumSettlementTableCustomActionConfig } from '../../settlements.models'
import { styles } from '../../settlements.styles'

const isCustomSelectionMode = (config: EnumSettlementTableCustomActionConfig) =>
  config === EnumSettlementTableCustomActionConfig.CUSTOM_SELECTION ||
  config === EnumSettlementTableCustomActionConfig.ERROR_SETTLEMENT

const isViewObjectionsMode = (config: EnumSettlementTableCustomActionConfig) =>
  config === EnumSettlementTableCustomActionConfig.VIEW_OBJECTIONS

interface SettlementsTableCardProps {
  title?: string
  data: SettlementTableModel[]
  isLoading?: boolean
  invoiceCycleType?: EnumTipoCicloFacturacion
  subjectType?: EnumTipoSujeto
  billingType?: EnumTipoFacturacion
  reInvoiceType?: EnumTipoRefacturacion
  groupingType?: EnumTipoAgrupacion
  generateInvoice?: boolean
  show?: boolean
  canDownload?: boolean
  isSettlementsInvoice?: boolean
  isSettlements?: boolean
  onViewObjectionsClick?: (settlementRow: SettlementTableModel) => void
  onViewCommentsClick?: (settlementRow: SettlementTableModel) => void
  onClickManualInvoiceActionHandler?: (settlementRow: SettlementTableModel) => void
  onTerminateObjectionsClick?: (selectedRows: SettlementTableModel[]) => void
  onRegenerateClick?: (selectedRows: SettlementTableModel[]) => void
  onRepaintClick?: (selectedRows: SettlementTableModel[]) => void
  onRejectClick?: (selectedRows: SettlementTableModel[]) => void
  onRevertDiscardedClick?: (selectedRows: SettlementTableModel[]) => void
  onRevertValidatedClick?: (selectedRows: SettlementTableModel[]) => void
  onValidateClick?: (selectedRows: SettlementTableModel[]) => void
  onDeleteClick?: (selectedRows: SettlementTableModel[]) => void
  onExitButtonClick?: () => void
  customToolbarActionsConfig?: EnumSettlementTableCustomActionConfig
  cycleIsFinished?: boolean
  onSelectionChange?: (selectedRows: SettlementTableModel[], selectedIndices: number[]) => void
  customFooterActions?: CardActionProps[]
  elevation?: number
  hideTitle?: boolean
  hideToolbar?: boolean
  disableSelection?: boolean
  // Ocultar columna de calidad de dato (depende de liquidaciones, no de facturas)
  hideQualityBad?: boolean
  // Campo identificador de fila (default: 'id' para liquidaciones; usar 'invoiceId' cuando no hay id de liquidación)
  rowIdField?: string
}

export const SettlementsTableCard: React.FC<SettlementsTableCardProps> = ({
  title = '',
  data,
  isLoading,
  invoiceCycleType,
  subjectType,
  billingType,
  reInvoiceType,
  groupingType,
  generateInvoice = false,
  show = true,
  canDownload = false,
  isSettlementsInvoice = false,
  isSettlements = false,
  onViewObjectionsClick,
  onViewCommentsClick,
  onClickManualInvoiceActionHandler,
  onTerminateObjectionsClick,
  onRegenerateClick,
  onRepaintClick,
  onRejectClick,
  onRevertDiscardedClick,
  onRevertValidatedClick,
  onValidateClick,
  onDeleteClick,
  onExitButtonClick,
  customToolbarActionsConfig =EnumSettlementTableCustomActionConfig.NOT_DEFINED,
  cycleIsFinished = false,
  onSelectionChange,
  customFooterActions = [],
  elevation,
  hideTitle = false,
  hideToolbar = false,
  disableSelection = false,
  hideQualityBad = false,
  rowIdField = 'id'
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements', 'settlementsInvoices'])
  const theme = useTheme()
  const useStyles = makeStyles(styles(theme))
  const classes = useStyles()
  const { error } = useNotify()

  const isCuentasOrFlexibilidad =
    invoiceCycleType === EnumTipoCicloFacturacion.Contratos ||
    invoiceCycleType === EnumTipoCicloFacturacion.Flexibilidad
  const isTrades = invoiceCycleType === EnumTipoCicloFacturacion.Trades
  const isProducto = invoiceCycleType === EnumTipoCicloFacturacion.Producto
  const isClienteMayorista = subjectType === EnumTipoSujeto.Mayorista

  const isFacturacionNormal = billingType === EnumTipoFacturacion.Normal
  const isRefacturacionOrAnuladora =
    billingType === EnumTipoFacturacion.Refacturacion ||
    billingType === EnumTipoFacturacion.Anuladora

  const isAnuladoraNueva = reInvoiceType === EnumTipoRefacturacion.AnuladoraNueva
  const isAjusteProxima = reInvoiceType === EnumTipoRefacturacion.AjusteProxima
  const isDiferencias = reInvoiceType === EnumTipoRefacturacion.Diferencia

  const grouped =
    isSettlementsInvoice || isRefacturacionOrAnuladora || (isFacturacionNormal && !isSettlements)
  const groupedByContract = groupingType === EnumTipoAgrupacion.Contrato

  // setState variables
  const [downloadMenuAnchorPosition, setDownloadMenuAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [invoiceRows, setInvoiceRows] = React.useState<SettlementTableModel[] | null>(null)

  // dataTable download options
  const downloadOptions = [
    {
      label: t('enums:EnumFileExtension.HTML'),
      icon: <LoupeOutlined />,
      fileExtension: EnumFileExtension.HTML
    }
  ]

  // #region ---------------------------  Download Menu Handlers -----------------------------------//
  const onDownloadMenuCloseHandler = useCallback(() => {
    setDownloadMenuAnchorPosition(null)
  }, [])

  const onDownloadActionHandler = useCallback(
    (row: SettlementTableModel | SettlementTableModel[], event?: any) => {
      const isArray = Array.isArray(row)
      const invoicesRows = isArray ? row : [row]
      setInvoiceRows(invoicesRows)

      // Calcular posición absoluta del botón (centrado horizontalmente)
      if (event?.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect()
        setDownloadMenuAnchorPosition({
          top: rect.bottom,
          left: rect.left + rect.width / 2 // Centrar el menú horizontalmente
        })
      }
    },
    []
  )

  const onDownloadMenuClickHandler = useCallback(
    async (fileExtension: EnumFileExtension) => {
      if (invoiceRows?.length === 1 && invoiceRows?.[0].invoiceId != null) {
        await downloadInvoiceDocument(invoiceRows?.[0].invoiceId!, fileExtension).then(response => {
          if (response.success && response.data) {
            fileDownloadHelper(response.data.name, response.data)
          } else {
            error(t('settlementsInvoices:fetchResponse.downloadInvoice.error'))
          }
        })
      } else if (invoiceRows && invoiceRows.length > 1) {
        const invoiceIds = invoiceRows.map(row => row.invoiceId!)
        const request = mapInvoiceIdsToDownloadFileRequestModel(invoiceIds, fileExtension)
        await downloadFiles(request).then(response => {
          if (response.success && response.data) {
            fileDownloadHelper(response.data.name, response.data)
          } else {
            error(t('settlementsInvoices:fetchResponse.downloadFiles.error'))
          }
        })
      }

      onDownloadMenuCloseHandler()
    },
    [invoiceRows, error, t, onDownloadMenuCloseHandler]
  )

  // #region DataTable Config Starts

  const rowCustomActions = useMemo((): RowCustomAction[] => {
    const actions: RowCustomAction[] = []

    if (canDownload) {
      actions.push({
        icon: <CloudDownload fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.downloadInvoice'),
        onClick: onDownloadActionHandler,
        userPermission: UserPermission.EDIT,
        visibleIf: 'invoiceId'
      })
    }

    if (onViewObjectionsClick) {
      actions.push({
        icon: <Warning fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.objections'),
        onClick: onViewObjectionsClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onClickManualInvoiceActionHandler && !cycleIsFinished) {
      actions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t(
          'settlementsInvoices:components.settlementsTableCard.tooltips.manualInvoiceDetail'
        ),
        visibleIf: 'canManual',
        onClick: onClickManualInvoiceActionHandler,
        userPermission: UserPermission.EDIT
      })
    }

    if (onViewCommentsClick) {
      actions.push({
        icon: <Message fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.comments'),
        onClick: onViewCommentsClick,
        userPermission: UserPermission.EDIT,
        visibleIf: 'invoiceId'
      })
    }

    if (isAnuladoraNueva) return actions

    if (onRegenerateClick && !cycleIsFinished) {
      actions.push({
        icon: <Autorenew fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.regenerate'),
        onClick: (row: SettlementTableModel) => onRegenerateClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'notIssued'
      })
    }

    if (onRepaintClick && !cycleIsFinished) {
      actions.push({
        icon: <Brush fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.repaint'),
        onClick: (row: SettlementTableModel) => onRepaintClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'notIssued'
      })
    }

    if (onRejectClick && !cycleIsFinished) {
      actions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.reject'),
        onClick: (row: SettlementTableModel) => onRejectClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'notIssued'
      })
    }

    if (onRevertDiscardedClick && !cycleIsFinished) {
      actions.push({
        icon: <Undo fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.revertDiscarded'),
        onClick: (row: SettlementTableModel) => onRevertDiscardedClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'canRevertDiscarded'
      })
    }

    if (onRevertValidatedClick && !cycleIsFinished) {
      actions.push({
        icon: <Undo fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.revertValidated'),
        onClick: (row: SettlementTableModel) => onRevertValidatedClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'canRevertValidated'
      })
    }

    if (onValidateClick && !cycleIsFinished) {
      actions.push({
        icon: <CheckCircleOutline fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.validateInvoice'),
        onClick: (row: SettlementTableModel) => onValidateClick([row]),
        userPermission: UserPermission.EDIT,
        visibleIf: 'canValidate'
      })
    }

    if (onDeleteClick) {
      actions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t(
          'settlementsInvoices:components.settlementsTableCard.tooltips.deleteErrorSettlement'
        ),
        onClick: (row: SettlementTableModel) => onDeleteClick([row]),
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }, [
    isAnuladoraNueva,
    onViewObjectionsClick,
    onViewCommentsClick,
    onClickManualInvoiceActionHandler,
    onDownloadActionHandler,
    onRegenerateClick,
    onRepaintClick,
    onRejectClick,
    onRevertDiscardedClick,
    onRevertValidatedClick,
    onValidateClick,
    onDeleteClick,
    canDownload,
    cycleIsFinished
  ])

  const groupCustomActions = useMemo((): GroupCustomAction[] => {
    if (!isAnuladoraNueva) return []
    const actions: GroupCustomAction[] = []
    if (onViewObjectionsClick) {
      actions.push({
        icon: <Warning fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.objections'),
        onClick: (_groupValue: any, leafRows: SettlementTableModel[]) => {
          if (leafRows[0]) onViewObjectionsClick(leafRows[0])
        },
        userPermission: UserPermission.EDIT
      })
    }
    if (onRegenerateClick && !cycleIsFinished) {
      actions.push({
        icon: <Autorenew fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.regenerate'),
        onClick: (_groupValue: any, leafRows: SettlementTableModel[]) => {
          onRegenerateClick([leafRows[0]])
        },
        userPermission: UserPermission.EDIT
      })
    }
    if (onRepaintClick && !cycleIsFinished) {
      actions.push({
        icon: <Brush fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.repaint'),
        onClick: (_groupValue: any, leafRows: SettlementTableModel[]) => {
          onRepaintClick([leafRows[0]])
        },
        userPermission: UserPermission.EDIT
      })
    }
    if (onRejectClick && !cycleIsFinished) {
      actions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.reject'),
        onClick: (_groupValue: any, leafRows: SettlementTableModel[]) => {
          onRejectClick([leafRows[0]])
        },
        userPermission: UserPermission.EDIT
      })
    }
    return actions
  }, [
    isAnuladoraNueva,
    onViewObjectionsClick,
    onRegenerateClick,
    onRepaintClick,
    onRejectClick,
    cycleIsFinished,
    t
  ])

  const customToolbarActions = useMemo((): CustomAction[] => {
    const actions: CustomAction[] = []

    if (canDownload && isCustomSelectionMode(customToolbarActionsConfig)) {
      actions.push({
        icon: <CloudDownload fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.downloadInvoice'),
        onClick: onDownloadActionHandler
      })
    }
    if (
      onRegenerateClick &&
      isCustomSelectionMode(customToolbarActionsConfig) &&
      !cycleIsFinished
    ) {
      actions.push({
        icon: <Autorenew fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.regenerate'),
        onClick: onRegenerateClick
      })
    }
    if (onRepaintClick && isCustomSelectionMode(customToolbarActionsConfig) && !cycleIsFinished) {
      actions.push({
        icon: <Brush fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.repaint'),
        onClick: onRepaintClick
      })
    }
    if (onRejectClick && isCustomSelectionMode(customToolbarActionsConfig) && !cycleIsFinished) {
      actions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('settlements:components.settlementsTableCard.dataTable.tooltips.reject'),
        onClick: onRejectClick
      })
    }
    if (
      onRevertValidatedClick &&
      isCustomSelectionMode(customToolbarActionsConfig) &&
      !cycleIsFinished
    ) {
      actions.push({
        icon: <Undo fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.revertValidated'),
        onClick: onRevertValidatedClick
      })
    }
    if (onValidateClick && isCustomSelectionMode(customToolbarActionsConfig) && !cycleIsFinished) {
      actions.push({
        icon: <CheckCircleOutline fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.validateInvoice'),
        onClick: onValidateClick
      })
    }
    if (
      onTerminateObjectionsClick &&
      (isCustomSelectionMode(customToolbarActionsConfig) ||
        isViewObjectionsMode(customToolbarActionsConfig)) &&
      !cycleIsFinished
    ) {
      actions.push({
        icon: <DoneAllOutlined fontSize="small" />,
        tooltip: t(
          'settlements:components.settlementsTableCard.dataTable.tooltips.terminateObjections'
        ),
        onClick: onTerminateObjectionsClick
      })
    }
    return actions
  }, [
    onRegenerateClick,
    onRepaintClick,
    onRejectClick,
    onRevertDiscardedClick,
    onRevertValidatedClick,
    onValidateClick,
    onTerminateObjectionsClick,
    onDownloadActionHandler,
    customToolbarActionsConfig,
    canDownload,
    cycleIsFinished
  ])

  const expandedSettlementGroups = useMemo(() => {
    const groups: Record<string, boolean> = {}
    data.forEach(row => {
      const groupKey = row.settlementGroupCode ?? ''
      groups[`settlementGroupCode:${groupKey}`] = true
    })
    return groups
  }, [data])

  const dataTableOptions: DataTableOptions = useMemo(
    () => ({
      search: true,
      pagination: true,
      rowsPerPage: 100,
      rowsPerPageOptions: [20, 50, 100],
      filterType: FilterType.ADVANCED,
      // PdteValidarListado siempre row; AnuladoraNueva y AjusteProxima siempre group por estructura de filas
      selectMode: isSettlements ? 'row' : isAnuladoraNueva || isAjusteProxima ? 'group' : 'row',
      selectableRows: !disableSelection,
      ...(grouped && {
        grouping:
          !isSettlements && !isRefacturacionOrAnuladora && !isFacturacionNormal
            ? ['settlementGroupCode']
            : !isSettlements && isAnuladoraNueva && !groupedByContract
              ? ['settlementGroupCode', 'originalInvoiceCode']
              : ['settlementGroupCode', 'contractCode'],
        expanded: expandedSettlementGroups
      }),
      rowCustomActions: rowCustomActions.length > 0 ? rowCustomActions : undefined,
      groupCustomActions: groupCustomActions.length > 0 ? groupCustomActions : undefined,
      selectToolbarCustomActions:
        customToolbarActions.length > 0 ? customToolbarActions : undefined,
      hideToolbar
    }),
    [
      rowCustomActions,
      groupCustomActions,
      customToolbarActions,
      hideToolbar,
      disableSelection,
      grouped,
      isSettlementsInvoice,
      isRefacturacionOrAnuladora,
      expandedSettlementGroups
    ]
  )

  const dataTableColumns = useMemo(() => {
    const columns: DataTableColumn[] = [
      {
        name: 'id',
        type: DataType.NUMBER,
        display: false
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML,
        columnWidth: 65
      },
      {
        name: 'originalInvoiceCode',
        label: t(
          'settlements:components.settlementsTableCard.dataTable.columns.originalInvoiceCode'
        ),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora && !groupedByContract && !isSettlements
      },
      {
        name: 'anullmentInvoiceCode',
        label: t(
          'settlements:components.settlementsTableCard.dataTable.columns.anullmentInvoiceCode'
        ),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora && !isAnuladoraNueva
      },
      {
        name: 'subject',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.subject'),
        type: DataType.TEXT,
        display: !isRefacturacionOrAnuladora && !isSettlementsInvoice && !isTrades && !isProducto,
        columnWidth: isSettlements ? 300 : undefined
      },
      {
        name: 'contractCode',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.contractCode'),
        type: DataType.TEXT,
        display: isCuentasOrFlexibilidad || isRefacturacionOrAnuladora || isSettlementsInvoice,
        columnWidth: 250
      },
      {
        name: 'tradeCode',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.tradeCode'),
        type: DataType.TEXT,
        display: isClienteMayorista || isTrades
      },
      {
        name: 'productCode',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.productCode'),
        type: DataType.TEXT,
        display:
          isProducto ||
          isTrades ||
          (isClienteMayorista && (isRefacturacionOrAnuladora || isSettlementsInvoice))
      },
      {
        name: 'startDate',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.endDate'),
        type: DataType.DATE
      },
      {
        name: 'invoiceState',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.invoiceState'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumEstadoFactura, 'enums:EnumEstadoFactura'),
        display: isRefacturacionOrAnuladora || isSettlementsInvoice
      },
      {
        name: 'invoiceCode',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.invoiceCode'),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice,
        customBodyRender: (value: any) =>
          value || t('settlements:components.settlementsTableCard.dataTable.noInvoiceGenerated')
      },
      {
        name: 'invoiceType',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.invoiceType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoFactura, 'enums:EnumTipoFactura'),
        display: isRefacturacionOrAnuladora || isSettlementsInvoice
      },
      {
        name: 'issuingSubject',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.issuingSubject'),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice,
        columnWidth: 200
      },
      {
        name: 'recipientSubject',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.recipientSubject'),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice,
        columnWidth: 200
      },
      {
        name: 'settlementGroupCode',
        label: t(
          'settlements:components.settlementsTableCard.dataTable.columns.settlementGroupCode'
        ),
        type: DataType.TEXT,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice,
        customBodyRender: (value: any) => value ?? '',
        columnWidth: 200
      },
      {
        name: 'issueDate',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.issueDate'),
        type: DataType.DATE,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice
      },
      {
        name: 'amount',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.amount'),
        type: DataType.NUMBER,
        display: isRefacturacionOrAnuladora || isSettlementsInvoice
      },
      {
        // qualityBad depende de datos de liquidaciones (settlements).
        // Se oculta cuando la tabla muestra datos provenientes de facturas (invoices).
        // Pasar hideQualityBad=true para desactivarla (ej: página de anulación).
        name: 'qualityBadRender',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.qualityBad'),
        type: DataType.HTML,
        columnWidth: 150,
        helpText: t('settlements:components.settlementsTableCard.dataTable.tooltips.qualityBad'),
        display: !hideQualityBad
      }
    ]

    let orderedColumns = [...columns]

    // Ordenamiento diferenciado según el tipo
    if (isRefacturacionOrAnuladora || !isSettlements) {
      const columnsOrder = isDiferencias
        ? [
            'actions',
            'settlementGroupCode',
            'contractCode',
            'invoiceState',
            'invoiceCode',
            'invoiceType',
            'issuingSubject',
            'recipientSubject',
            'tradeCode',
            'productCode',
            'issueDate',
            'startDate',
            'endDate',
            'amount',
            'qualityBadRender',
            'subject',
            'id'
          ]
        : isAnuladoraNueva || isAjusteProxima
          ? [
              'actions',
              'settlementGroupCode',
              'originalInvoiceCode',
              'contractCode',
              'invoiceState',
              'invoiceCode',
              'invoiceType',
              'issuingSubject',
              'recipientSubject',
              'tradeCode',
              'productCode',
              'issueDate',
              'startDate',
              'endDate',
              'amount',
              'qualityBadRender',
              'subject',
              'id'
            ]
          : [
              'actions',
              'settlementGroupCode',
              'contractCode',
              'invoiceState',
              'invoiceCode',
              'invoiceType',
              'issuingSubject',
              'recipientSubject',
              'contractCode',
              'tradeCode',
              'productCode',
              'issueDate',
              'startDate',
              'endDate',
              'amount',
              'qualityBadRender',
              'subject',
              'id'
            ]

      orderedColumns.sort((a, b) => {
        const posA = columnsOrder.indexOf(a.name)
        const posB = columnsOrder.indexOf(b.name)
        return (posA === -1 ? 999 : posA) - (posB === -1 ? 999 : posB)
      })
    }

    return orderedColumns
  }, [
    t,
    grouped,
    isAnuladoraNueva,
    isAjusteProxima,
    isRefacturacionOrAnuladora,
    isSettlementsInvoice,
    billingType,
    isCuentasOrFlexibilidad,
    isTrades,
    isProducto,
    isClienteMayorista
  ])

  // #endregion DataTable Config Ends

  // #region Helpers Starts
  const settlementTableCardDataFormatter = useCallback(
    (data: SettlementTableModel[]) => {
      // Definir jerarquía de estados (del más importante al menos)
      const stateHierarchy: { [key in EnumEstadoFactura]: number } = {
        [EnumEstadoFactura.Borrador]: 1,
        [EnumEstadoFactura.Emitida]: 2,
        [EnumEstadoFactura.Validada]: 3,
        [EnumEstadoFactura.Conciliada]: 4,
        [EnumEstadoFactura.Pagada]: 5,
        [EnumEstadoFactura.Generada]: 6,
        [EnumEstadoFactura.Incompleta]: 7,
        [EnumEstadoFactura.Rechazada]: 8,
        [EnumEstadoFactura.Finalizada]: 9,
        [EnumEstadoFactura.Impago]: 10,
        [EnumEstadoFactura.Descartada]: 11,
        [EnumEstadoFactura.Anulada]: 12
      }

      // Agregar el campo qualityBadRender para mostrar el Rating component
      // También reemplazar settlementGroupCode y contractCode null con valor especial para agrupación
      const dataWithRating = data.map(row => ({
        ...row,
        qualityBadRender: (
          <Rating
            name="quality-rating"
            value={row.qualityBad ?? 0}
            readOnly
            size="small"
            precision={1}
            max={5}
          />
        )
      }))

      return dataWithRating.sort((a, b) => {
        // Para refacturación o anuladora: ordenar por fecha de emisión ascendente
        if (isRefacturacionOrAnuladora) {
          const dateA = a.issueDate ? new Date(a.issueDate).getTime() : 0
          const dateB = b.issueDate ? new Date(b.issueDate).getTime() : 0
          return dateA - dateB
        }

        // Ordenamiento por defecto: ordenar por invoiceId (id_factura)
        const invoiceIdA = a.invoiceId ?? 0
        const invoiceIdB = b.invoiceId ?? 0
        return invoiceIdA - invoiceIdB

        /* CÓDIGO ANTERIOR - COMENTADO POR SI SE NECESITA VOLVER ATRÁS:
        // Ordenamiento por jerarquía de estados + código de factura
        // Primer criterio: jerarquía de estados
        const stateA = a.invoiceState ?? 1
        const stateB = b.invoiceState ?? 1

        const hierarchyA = stateHierarchy[stateA] || 999
        const hierarchyB = stateHierarchy[stateB] || 999

        if (hierarchyA !== hierarchyB) {
          return hierarchyA - hierarchyB
        }

        // Segundo criterio: código de factura
        const codeA = a.invoiceCode ?? ''
        const codeB = b.invoiceCode ?? ''

        // Extraer el año (asumiendo formato BYYYYNNNNNN)
        const yearA = parseInt(codeA.slice(1, 5), 10) || 0
        const yearB = parseInt(codeB.slice(1, 5), 10) || 0

        // Ordenar por año descendente (más actual primero)
        if (yearA !== yearB) {
          return yearB - yearA
        }

        // Si el año es igual, ordenar por el resto del código numéricamente
        const numA = parseInt(codeA.slice(5), 10) || 0
        const numB = parseInt(codeB.slice(5), 10) || 0
        return numB - numA
        */
      })
    },
    [isRefacturacionOrAnuladora]
  )

  // #endregion Helpers Ends
  const footerActions = useMemo(() => {
    const actions: CardActionProps[] = []
    if (onExitButtonClick) {
      actions.push({
        label: t('settlements:components.settlementsTableCard.buttons.exit'),
        handleActionClick: onExitButtonClick,
        hasPermission: true
      })
    }
    return [...actions, ...customFooterActions]
  }, [onExitButtonClick, customFooterActions])

  const formattedTableData = isLoading ? undefined : settlementTableCardDataFormatter(data)

  return show ? (
    <>
      <CardWithTransition show={show} unmountOnExit={true} elevation={elevation}>
        <GlobDataTable
          title={
            hideTitle ? undefined : title ? (
              title
            ) : (
              <Typography variant="h6">
                {t('settlements:components.settlementsTableCard.title')}
              </Typography>
            )
          }
          data={formattedTableData}
          columns={dataTableColumns}
          options={dataTableOptions}
          onSelectionChange={onSelectionChange}
          rowIdField={isAnuladoraNueva ? 'rowKey' : rowIdField}
        />
        <CardBottom actions={footerActions} />
      </CardWithTransition>

      <Menu
        keepMounted={false}
        elevation={2}
        open={Boolean(downloadMenuAnchorPosition)}
        onClose={onDownloadMenuCloseHandler}
        anchorReference="anchorPosition"
        anchorPosition={downloadMenuAnchorPosition || undefined}
      >
        {downloadOptions.map((downloadOption, index) => (
          <MenuItem
            dense
            key={index}
            onClick={() => onDownloadMenuClickHandler(downloadOption.fileExtension)}
          >
            <Typography variant="inherit">{downloadOption.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    <></>
  )
}
